import { useState, useMemo, useCallback, useContext, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { notification } from "@findemdev/maui";
// style
import {
  CredBuilderContainer,
  FilterWrapper,
  Heading,
  NewCredentialButton,
  GridContainer,
  ContentArea
} from "./Credentials.styled";
// components
import Filters from "./Filters";
import CredentialsTable from "./Table/CredentialsTable";
import CredentialsGrid from "../CredentialsGrid/CredentialsGrid";
import TitleSection from "./TitleSection";
import NavigationTabs, { TabId } from "./NavigationTabs";
// types
import { IFilterObj } from "./Filters/types";
import { ISearchFilter, ISearchSortFilter } from "../../../../types/shared";
import { CREDENTIAL_TAB_KEYS, CreateCredentialHandler, CredentialsViewMode } from "../../types/common";
// constants
import { CREDENTIALS_PER_PAGE, GRID_CREDENTIALS_PER_PAGE, QueryParams } from "../../util/constants";
// apis
import { matchesApi, useCreateCredMutation } from "../../../../services/matches";
// hooks
import usePopulateCredsList from "../../hooks/usePopulateCredsList";
// utils
import { generateAutoCredentialName } from "../../util/common";
import useGetCredentialJoid from "../../hooks/useGetCredentialJoid";
import {
  buildInsightAssignmentLabelRequirement,
  decodeInsightProjectSearchLabels,
  INSIGHT_PROJECT_SEARCH_QUERY_PARAM,
} from "../../util/insightProjectSearch";
import { setSelectedUserId } from "../../reducers/credentialAppSlice";
import UserContext from "../../../../contexts/UserContext";

const CredLibraryFilterObj: IFilterObj = {
  [TabId["cred-library"]]: {
    field: "libraries",
    operator: "eq",
    value: "CRED_LIBRARY",
  },
};
const CredMarqueeFilterObj: IFilterObj = {
  [TabId["marquees"]]: {
    field: "libraries",
    operator: "eq",
    value: "MARQUEE_LIBRARY",
  },
};
const CredFavoritesFilterObj = ({ value }: { value: string }): IFilterObj => ({
  [TabId["favorites"]]: {
    field: "favorite_marked_by",
    operator: "eq",
    value: value,
  },
});
const CREDENTIAL_DETAIL_TAB_QUERY_PARAM = "tab";
// Get initial filter based on the initial tab
const getInitialFilter = ({ tabId, favoritesValue }: { tabId: TabId, favoritesValue: string }): IFilterObj => {
  if (tabId === TabId["cred-library"]) {
    return CredLibraryFilterObj;
  } else if (tabId === TabId["marquees"]) {
    return CredMarqueeFilterObj;
  } else if (tabId === TabId["favorites"]) {
    return CredFavoritesFilterObj({ value: favoritesValue });
  }
  return {};
};

// Get initial tab from URL query parameter or fallback to default
const getInitialTab = ({ homeTab }: { homeTab: string | null}): TabId => {
  // Validate if homeTab is a valid TabId enum value
  if (homeTab && Object.values(TabId).includes(homeTab as TabId)) {
    return homeTab as TabId;
  }
  return TabId["my-credentials"];
};
const Credentials = () => {
  const dispatch = useDispatch();
  const userModel = useContext(UserContext);
  const navigate = useNavigate();
  const credentialJoid = useGetCredentialJoid({});
  const [searchParams, setSearchParams] = useSearchParams();
  const handledInsightQueryRef = useRef<string | null>(null);

  const initialTab = getInitialTab({ homeTab: searchParams.get(QueryParams.HomeTab) });
  const [filterObj, setFilterObj] = useState<IFilterObj>(getInitialFilter({ tabId: initialTab, favoritesValue: credentialJoid }));
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [sortFields, setSortFields] = useState<ISearchSortFilter>();
  const [activeViewMode, setActiveViewMode] = useState<CredentialsViewMode>('list');
  const [activeTab, setActiveTab] = useState<TabId>(initialTab);
  const insightProjectSearchQuery = searchParams.get(INSIGHT_PROJECT_SEARCH_QUERY_PARAM);

  useEffect(() => {
    setSearchParams((prev) => {
      const nextSearchParams = new URLSearchParams(prev);
      nextSearchParams.set(QueryParams.HomeTab, activeTab);
      return nextSearchParams;
    }, { replace: true });
  }, []);

  // memo
  const filters: ISearchFilter[] | undefined = useMemo(() => {
    const _filters: ISearchFilter[] = [];
    Object.values(filterObj).forEach(
      (filter: ISearchFilter | undefined) => {
        if (filter && filter.value) {
          _filters.push(filter);
        }
      }
    );
    return _filters;
  }, [filterObj]);

  const credentialsPageSize = activeViewMode === 'grid' ? GRID_CREDENTIALS_PER_PAGE : CREDENTIALS_PER_PAGE;

  const { credentialsData, isCredentialsPending } =
    usePopulateCredsList({
      pageNumber,
      joid: credentialJoid,
      pageSize: credentialsPageSize,
      filters: filters,
      sortFields,
      activeTab,
    });
  const [createCredential, { isLoading: isCreateCredentialPending}] = useCreateCredMutation();

  const handleViewModeChange = useCallback((ev: React.MouseEvent<HTMLButtonElement>) => {
    const target = ev.currentTarget as HTMLElement;
    setActiveViewMode(target.getAttribute("data-tab") as CredentialsViewMode);
    setPageNumber(0);
  }, []);

  const handleTabChange = (tabId: TabId) => {
    setActiveTab(tabId);
    setPageNumber(0);
    setSearchParams({ homeTab: tabId });

    if (tabId === TabId["cred-library"]) {
      setFilterObj(CredLibraryFilterObj);
    } else if (tabId === TabId["marquees"]) {
      setFilterObj(CredMarqueeFilterObj);
    } else if (tabId === TabId["favorites"]) {
      setFilterObj(CredFavoritesFilterObj({ value: credentialJoid }));
    } else {
      setFilterObj({});
    }
  };

  const handleUserChange = useCallback((userId: string | undefined) => {
    dispatch(setSelectedUserId(userId));
    setPageNumber(0);

    if (activeTab === TabId["favorites"]) {
      setFilterObj(CredFavoritesFilterObj({ value: userId ?? userModel.getUser().uid }));
    }
  }, [activeTab, dispatch, userModel]);

  const handleSorting = useCallback((sortObj: ISearchSortFilter | undefined) => {
    setSortFields(sortObj);
  }, []);

  const createCredentialHandler: CreateCredentialHandler = async () => {
    try {
      const resp = await createCredential({
        name: generateAutoCredentialName(),
        client_company_name: undefined,
        type: undefined,
        industry: undefined,
        practice: undefined,
        capability: undefined,
        joid: credentialJoid,
      }).unwrap();

      if (resp.data) {
        dispatch(matchesApi.util.invalidateTags(["Credentials"]));
        navigate(`credentials/${resp.data?.ref}?${searchParams.toString()}`);
      }
    } catch (error) {
      console.error("error while creating credential: ", error);
      notification.error({
        message: "Error while creating credential",
      });
    }
  };

  useEffect(() => {
    if (!insightProjectSearchQuery || handledInsightQueryRef.current === insightProjectSearchQuery) {
      return;
    }

    handledInsightQueryRef.current = insightProjectSearchQuery;

    const createCredentialFromInsightProjectSearch = async () => {
      const labels = decodeInsightProjectSearchLabels(insightProjectSearchQuery);
      const assignmentLabelRequirement = buildInsightAssignmentLabelRequirement(labels);

      if (!assignmentLabelRequirement) {
        notification.error({
          message: "Unable to open Insight project labels",
        });
        setSearchParams((prev) => {
          const nextSearchParams = new URLSearchParams(prev);
          nextSearchParams.delete(INSIGHT_PROJECT_SEARCH_QUERY_PARAM);
          nextSearchParams.set(QueryParams.HomeTab, activeTab);
          return nextSearchParams;
        }, { replace: true });
        return;
      }

      try {
        const resp = await createCredential({
          name: generateAutoCredentialName(),
          client_company_name: undefined,
          type: undefined,
          industry: undefined,
          practice: undefined,
          capability: undefined,
          joid: credentialJoid,
          requirements: [assignmentLabelRequirement],
        }).unwrap();

        if (resp.data) {
          const detailSearchParams = new URLSearchParams({
            [QueryParams.HomeTab]: activeTab,
            [CREDENTIAL_DETAIL_TAB_QUERY_PARAM]: CREDENTIAL_TAB_KEYS.all_assignments,
          });

          dispatch(matchesApi.util.invalidateTags(["Credentials"]));
          navigate(`credentials/${resp.data.ref}?${detailSearchParams.toString()}`, { replace: true });
        }
      } catch (error) {
        console.error("error while creating credential from Insight project search: ", error);
        notification.error({
          message: "Error while creating credential",
        });
      }
    };

    void createCredentialFromInsightProjectSearch();
  }, [
    activeTab,
    createCredential,
    credentialJoid,
    dispatch,
    insightProjectSearchQuery,
    navigate,
    setSearchParams,
  ]);

  const handlePageChange = useCallback(
    (page: number, _pageSize: number): void => {
      setPageNumber(page - 1);
    },
    []
  );

  return (
    <CredBuilderContainer>
      <Helmet>
        <title>Credentials | Findem</title>
      </Helmet>
      <Heading>
        <div>
          <h2>Credential Builder</h2>
          <p>
            Search the entire RRA work history and effortlessly generate
            personalized Credential presentations.
          </p>
        </div>
        <NewCredentialButton
          type="primary"
          onClick={createCredentialHandler}
          loading={isCreateCredentialPending}
          disabled={isCreateCredentialPending}
        >
          Create New Credential
        </NewCredentialButton>
      </Heading>
      <GridContainer $addGrid={true}>
        <NavigationTabs activeTab={activeTab} onTabChange={handleTabChange} />
        <ContentArea>
          <TitleSection
            activeViewMode={activeViewMode}
            activeTab={activeTab}
            credentialsData={credentialsData}
            filterObj={filterObj}
            setFilterObj={setFilterObj}
            handleTabChange={handleViewModeChange}
            onUserChange={handleUserChange}
          />
          <FilterWrapper>
            <Filters
              onFilterChange={setFilterObj}
              filterObj={filterObj}
              activeTab={activeTab}
            />
          </FilterWrapper>
          {activeViewMode === 'list' ?
            <CredentialsTable
              data={credentialsData?.data ?? []}
              total={credentialsData?.count ?? 0}
              isPending={isCredentialsPending}
              handleSorting={handleSorting}
              pageNumber={pageNumber}
              pageChange={handlePageChange}
              activeTab={activeTab}
            />
            :
            <CredentialsGrid
              pageNumber={pageNumber}
              data={credentialsData?.data ?? []}
              total={credentialsData?.count ?? 0}
              pageSize={credentialsPageSize}
              handlePageChange={handlePageChange}
              isPending={isCredentialsPending}
            />
          }
        </ContentArea>
      </GridContainer>
    </CredBuilderContainer>
  );
};

export default Credentials;
